import { debounce } from 'underscore';
import { SecondMs } from 'rev-shared/date/Time.Constant';
import { isSafari } from 'rev-shared/util/UserAgentUtil';
import { BehaviorSubject, Observable } from 'rxjs';

const frameRate = 30;
const frameTime = SecondMs / frameRate;
const slowFrameTime = 5 * frameTime;
const slowFrameStrikes = 5;

export interface IVideoStreamProcessor {
	video: HTMLVideoElement;
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D;

	outputStream$: Observable<MediaStream>;
	width: number;
	height: number;

	destroy(): void;
	disable(): void;
	enable(): void;
}

export function tryGetVideoProcessor(
	stream: MediaStream,
	processFrame: (p: IVideoStreamProcessor) => void
): IVideoStreamProcessor {

	if(isSafari() || !stream || !processFrame) {
		return;
	}
	class VideoStreamProcessor implements IVideoStreamProcessor {
		public video: HTMLVideoElement;
		public canvas: HTMLCanvasElement;
		public ctx: CanvasRenderingContext2D;

		private readonly outputStreamSubject$ = new BehaviorSubject<MediaStream>(null);
		public readonly outputStream$ = this.outputStreamSubject$.asObservable();
		private readonly processorStream: MediaStream;
		public width: number;
		public height: number;
		private initialized: boolean;
		private timer: number;
		private enabled: boolean;
		private slowTest: () => boolean;
		private unSlowTime: number;

		private readonly videoResized = debounce(() => this.tryStartAnimation(), 1000);

		constructor() {
			this.video = document.createElement('video');
			this.canvas = document.createElement('canvas');
			this.ctx = this.canvas.getContext('2d');
			this.outputStreamSubject$.next(stream);
			this.processorStream = this.canvas.captureStream(frameRate);
			this.video.muted = true;
			this.video.autoplay = true;
			this.video.srcObject = stream;
			this.video.addEventListener('loadedmetadata', () => this.init());
			this.video.addEventListener('resize', this.videoResized, false);
			this.video.play();
		}

		public enable(): void {
			if(this.enabled) {
				return;
			}

			this.enabled = true;
			this.outputStreamSubject$.next(this.processorStream);
			this.tryStartAnimation();
		}

		public disable(): void {
			if(!this.enabled) {
				return;
			}
			this.enabled = false;
			this.outputStreamSubject$.next(stream);
		}

		private checkAnimationSlow(): void {
			const slow = this.slowTest();
			const alreadySlow = this.unSlowTime > 0;
			if(!alreadySlow && slow) {
				this.unSlowTime = Date.now() + 1000;
				this.outputStreamSubject$.next(stream);
			}
			else if(!slow && alreadySlow && this.unSlowTime < Date.now()) {
				this.unSlowTime = null;
				this.outputStreamSubject$.next(this.processorStream);
			}
		}

		private init(): void {
			if(this.initialized) {
				return;
			}
			this.initialized = true;
			this.tryStartAnimation();
		}

		private tryStartAnimation(): void {
			if(!this.enabled || !this.initialized) {
				return;
			}
			this.updateSize();
			this.resumeAnimation();
		}


		private resumeAnimation(): void {
			this.slowTest = slowTest(slowFrameTime, slowFrameStrikes);
			this.unSlowTime = null;
			this.animate();
		}

		private updateSize(): void {
			const canvas = this.canvas;
			const { videoWidth, videoHeight } = this.video;
			this.width = canvas.width = videoWidth;
			this.height = canvas.height = videoHeight;
		}

		private animate() {
			this.timer = window.setTimeout(() => {
				if(!this.enabled || !this.initialized) {
					return;
				}
				this.checkAnimationSlow();
				this.render();
				this.animate();
			}, frameTime);
		}

		private render(): void {
			this.ctx.drawImage(this.video, 0, 0, this.width, this.height);
			processFrame(this);
		}

		public destroy(): void {
			this.video = null as any;
			this.canvas = null as any;
			this.initialized = false;
			clearTimeout(this.timer);
		}
	}

	return new VideoStreamProcessor();
}

function slowTest(time: number, strikes: number): () => boolean {
	let lastTime = Date.now();
	let slowCount = 0;
	return () => {
		const t = Date.now();
		slowCount = t - lastTime < time ? 0 : slowCount + 1;
		lastTime = t;
		return slowCount >= strikes;
	};
}
